import {
  AccessTime,
  ArrowForward,
  CheckCircleOutlineOutlined,
  DescriptionOutlined,
  ErrorOutlineOutlined,
  PermDataSettingOutlined,
  TimerOffOutlined,
  TimerOutlined,
} from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  IconButton,
  Paper,
  Skeleton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as dayjs from "dayjs";
import { useStyle } from "./styles";
import ReportFilters from "../../../../componentes/easyReport/reportFilters";
import api from "../../../../services/api";
import { ReactComponent as EmptyBox } from "../../../../assets/easyReport/iconSvg/emptyBox.svg";
import { ptBR } from "@mui/x-data-grid/locales";

export interface AgendamentoRelatorio {
  id: string;
  relatorioId: string;
  agendamentoId?: string;
  ocorrencias?: string;
  dataGeracao: Date;
  dataInicialColeta: Date;
  perfil: string[];
  hosts: string[];
  items: string[];
  status: number;
  etapa: number;
  titulo: string | null;
}

enum IntervaloColeta {
  day = "Diário",
  week = "Semanalmente",
  month = "Mensalmente",
}

export default function AgendamentoDetalhado() {
  const [title, setTitle] = useState<string>("");
  const [rows, setRows] = useState<AgendamentoRelatorio[]>([]);
  const [filtroDataInicial, setFiltroDataInicial] =
    useState<dayjs.Dayjs | null>(null);
  const [filtroDataFinal, setFiltroDataFinal] = useState<dayjs.Dayjs | null>(
    null,
  );
  const [dataInicial, setDataInicial] = useState<dayjs.Dayjs | null>(null);
  const [dataFinal, setDataFinal] = useState<dayjs.Dayjs | null>(null);
  const [tipoColeta, setTipoColeta] = useState<string>("");
  const [loadingPage, setLoadingPage] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(10);
  const [
    relatoriosGeradosDataUltrapassada,
    setRelatoriosGeradosDataUltrapassada,
  ] = useState<boolean>(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const styles = useStyle(theme);
  const { id } = useParams<{ id: string }>();

  const columns: GridColDef<AgendamentoRelatorio>[] = [
    {
      field: "intervaloColeta",
      headerName: "Intervalo de Coleta",
      sortable: false,
      width: 240,
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => (
        <Box sx={styles.boxRenderCell}>
          <Typography sx={styles.typography}>
            {dayjs(params.row.dataInicialColeta).format("DD/MM/YYYY")}
          </Typography>
          <ArrowForward
            sx={{ fontSize: "1.1rem", color: theme.palette.text.primary }}
          />
          <Typography sx={styles.typography}>
            {dayjs(params.row.dataGeracao).format("DD/MM/YYYY")}
          </Typography>
        </Box>
      ),
    },
    {
      field: "profiles",
      headerName: "Perfil de Relatório",
      minWidth: 150,
      flex: 1,
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => (
        <Box sx={styles.boxRenderCell}>
          {Array.isArray(params.value) &&
            params.value.map((perfil) => (
              <Tooltip title={perfil}>
                <Chip
                  key={perfil}
                  label={perfil}
                  sx={{
                    ...styles.chip,
                    borderRadius: "7px",
                    maxWidth: "100px",
                  }}
                />
              </Tooltip>
            ))}
        </Box>
      ),
    },
    {
      field: "hosts",
      headerName: "Hosts",
      minWidth: 200,
      flex: 2,
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => {
        const value = params.value || [];
        const display = value.join(", ");
        return (
          <Tooltip title={display}>
            <Box
              sx={{
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                width: "100%",
              }}
            >
              {display}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "items",
      headerName: "Itens",
      minWidth: 300,
      flex: 3,
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => {
        return (
          <Box sx={styles.boxRenderCell}>
            {params.value?.map((item: string) => (
              <Tooltip title={item}>
                <Chip sx={styles.chip} label={item} key={item} />
              </Tooltip>
            ))}
          </Box>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 60,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => (
        <Box sx={styles.boxRenderCellIcon}>
          {params.row.status === 1 ? (
            <Tooltip title="Relatório Gerado">
              <CheckCircleOutlineOutlined color="success" />
            </Tooltip>
          ) : params.row.status === 2 ? (
            <Tooltip title="Erro ao gerar relatório">
              <ErrorOutlineOutlined color="error" />
            </Tooltip>
          ) : params.row.status === 0 ? (
            <Tooltip title="Relatório Não gerado">
              <TimerOutlined />
            </Tooltip>
          ) : (
            <Tooltip title="Relatório ultrapassou data">
              <TimerOffOutlined sx={{ color: theme.palette.text.disabled }} />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: "etapa",
      headerName: "Etapa",
      width: 60,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => (
        <Box sx={styles.boxRenderCellIcon}>
          {`${params.row.etapa}/${rows.length}`}
        </Box>
      ),
    },
    {
      field: "visualizar",
      headerName: "Ações",
      headerAlign: "center",
      width: 85,
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => {
        const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          try {
            const response = await api.post(
              "/easyReport/v2/downloadReport",
              { id: params.row.relatorioId },
              { responseType: "blob" },
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
              "download",
              `${params.row.titulo || "relatorio"}.pdf`,
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
          } catch (error) {
            console.error("Erro ao baixar relatório:", error);
          }
        };
        return (
          <Tooltip title="Visualizar">
            <Box sx={styles.boxRenderCellIcon}>
              <IconButton
                disabled={params.row.status !== 1}
                onClick={handleClick}
              >
                <DescriptionOutlined
                  color={params.row.status !== 1 ? "disabled" : "primary"}
                />
              </IconButton>
            </Box>
          </Tooltip>
        );
      },
    },
  ];

  useEffect(() => {
    const fetchAppointment = async () => {
      try {
        setLoadingPage(true);
        const res = await api.post("/easyReport/v2/findSchedule", { id: id });
        setTitle(res.data.titulo);
        setTipoColeta(res.data.tipoColeta);
        setDataInicial(res.data.dataInicial);
        setDataFinal(res.data.dataFinal);
        setRows(res.data.relatorios);
        setRelatoriosGeradosDataUltrapassada(
          res.data.relatoriosGeradosDataUltrapassada,
        );
      } catch (error) {
        console.error("Erro ao buscar relatórios:", error);
      } finally {
        setLoadingPage(false);
      }
    };
    fetchAppointment();
  }, [id]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const rowDataInicial = dayjs(row.dataInicialColeta);
      const rowDataFinal = dayjs(row.dataGeracao);

      const matchesDataInicial = filtroDataInicial
        ? rowDataInicial.isSameOrAfter(filtroDataInicial, "day")
        : true;
      const matchesDataFinal = filtroDataFinal
        ? rowDataFinal.isSameOrBefore(filtroDataFinal, "day")
        : true;

      return matchesDataInicial && matchesDataFinal;
    });
  }, [rows, filtroDataInicial, filtroDataFinal]);

  const handleClickConfiguracoes = () => {
    navigate(`/easyReportV2/agendamento/editar/${id}`);
  };
  function CustomNoRowsOverlay() {
    return (
      <Box sx={styles.boxNoRowsOverlay}>
        <EmptyBox style={styles.iconUserNotFound} />
        <Typography color="text.disabled">
          Nenhum relatório até o momento
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
        <Breadcrumbs
          separator={<Typography sx={styles.titleAndLinkPage}>/</Typography>}
        >
          <Button
            variant="text"
            onClick={() => navigate("/easyReportV2/agendamento")}
            sx={{
              ...styles.titleAndLinkPage,
              color: theme.palette.text.secondary,
              fontSize: 18,
              ":hover": {
                backgroundColor: "transparent",
                color: theme.palette.text.primary,
              },
            }}
          >
            Agendamento
          </Button>
          <Typography
            sx={{
              ...styles.titleAndLinkPage,
              color: theme.palette.text.primary,
            }}
          >
            Detalhes Agendamento
          </Typography>
        </Breadcrumbs>
        {/* <IconButton onClick={() => navigate("/easyReportV2/agendamento")}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5">{title}</Typography> */}
      </Box>
      {loadingPage ? (
        <Skeleton variant="rounded" width={"100%"} height={400} />
      ) : (
        <Paper sx={styles.paper}>
          <Box sx={styles.boxHeader}>
            <Box sx={styles.boxSearchAndFilter}>
              <Box sx={styles.boxData}>
                <Box sx={styles.boxDataHeader}>
                  <Typography
                    sx={{
                      ...styles.typography,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {dataInicial
                      ? dayjs(dataInicial).toDate().toLocaleDateString()
                      : ""}
                  </Typography>
                  <ArrowForward sx={{ fontSize: "1.1rem" }} />
                  <Typography
                    sx={{
                      ...styles.typography,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {dataFinal
                      ? dayjs(dataFinal).toDate().toLocaleDateString()
                      : ""}
                  </Typography>
                </Box>
                <Box sx={styles.boxDataHeader}>
                  <AccessTime />
                  <Typography>
                    {
                      IntervaloColeta[
                        tipoColeta as keyof typeof IntervaloColeta
                      ]
                    }
                  </Typography>
                </Box>
              </Box>
              <Box sx={styles.boxData}>
                <ReportFilters
                  filtroDataFinal={filtroDataFinal}
                  setFiltroDataFinal={setFiltroDataFinal}
                  filtroDataInicial={filtroDataInicial}
                  setFiltroDataInicial={setFiltroDataInicial}
                />
                <Button
                  onClick={handleClickConfiguracoes}
                  sx={{ gap: 1 }}
                  variant="contained"
                  disabled={relatoriosGeradosDataUltrapassada}
                >
                  <PermDataSettingOutlined />
                  Configurações
                </Button>
              </Box>
            </Box>
          </Box>
          <Box sx={{ width: "100%", height: "65vh", overflowX: "auto" }}>
            <DataGrid
              sx={styles.dataGrid}
              rows={filteredRows}
              columns={columns}
              loading={loadingPage}
              disableColumnMenu={true}
              pageSizeOptions={[10, 25, 50, 100]}
              onPaginationModelChange={(model) => setPageSize(model.pageSize)}
              localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0
                  ? "even-row"
                  : "odd-row"
              }
              slots={{
                noRowsOverlay: CustomNoRowsOverlay,
              }}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: pageSize },
                },
              }}
            />
          </Box>
        </Paper>
      )}
    </>
  );
}
